import {
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { MoreVert, SearchOutlined } from "@mui/icons-material";
import {
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  MenuList,
  MenuItem,
  Popover,
  Button,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { appThemes, useMeetingAppContext } from "../../MeetingAppContextDef";
import { RaiseHand } from "../../icons";
import { List } from "react-virtualized";
import useWindowSize from "../../utils/useWindowSize";
import useIsTab from "../../utils/useIsTab";
import useIsMobile from "../../utils/useIsMobile";
import ConfirmBox from "../../components/ConfirmBox";
import { nameTructed } from "../../utils/common";
import ToggleModeContainer from "../../components/ToggleModeContainer";
import { meetingModes } from "../../CONSTS";
import ParticipantScreenShareIcon from "../../icons/ParticipantScreenShareIcon";
import ParticipantMicOnIcon from "../../icons/ParticipantMicOnIcon";
import ParticipantMicOffIcon from "../../icons/ParticipantMicOffIcon";
import ParticipantVideoOnIcon from "../../icons/ParticipantVideoOnIcon";
import ParticipantVideoOffIcon from "../../icons/ParticipantVideoOffIcon";
import ParticipantPinIcon from "../../icons/ParticipantPinIcon";
import ParticipantRemoveIcon from "../../icons/ParticipantRemoveIcon";
import useIsHls from "../useIsHls";
import useCustomTrack from "../../utils/useCustomTrack";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import ConfigTabPanel from "./ConfigTabPanel";
import { useAsyncError, useLocation } from "react-router-dom";
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import PersonRemoveAlt1RoundedIcon from '@mui/icons-material/PersonRemoveAlt1Rounded';
import { addNoParticipantStartTime, updateParticipantsEndTime } from "../../../Services/MeetingApi";
import RequestedEntries from "../../components/RequestedEntries";
import { border } from "@mui/system";

function ParticipantListItem({ raisedHand, participantId }) {
  const { presenterId } = useMeeting();
  const mMeeting = useMeeting();
  const {
    participant,
    micOn,
    webcamOn,
    displayName,
    isLocal,
    pinState,
    webcamStream,
    pin,
    unpin,
    enableMic,
    disableMic,
    enableWebcam,
    disableWebcam,
  } = useParticipant(participantId);

  const {
    participantCanToggleOtherMic,
    participantCanToggleOtherWebcam,
    partcipantCanToogleOtherScreenShare,
    participantCanToggleOtherMode,
    canRemoveOtherParticipant,
    canPin,
    animationsEnabled,
    meetingMode,
    appTheme,
    participantCanToggleSelfMic,
    participantCanToggleSelfWebcam,
    selectMicDeviceId,
    selectWebcamDeviceId,
    addManualStageParticipant,
    removeManualStageParticipant,
    manualStageParticipantIds,
    addRemovedFromStageParticipant,
    clearRemovedFromStageParticipant,
    removedFromStageParticipantIds,
    mainViewParticipants,
  } = useMeetingAppContext();

  const { getCustomVideoTrack, getCustomAudioTrack } = useCustomTrack();
  // const SampleMeetingView = () => {
  //   //Getting all the participants
  //   const { participants } = useMeeting();

  //   //Looping over the participants and rendering a simple box
  //   return (
  //     <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)" }}>
  //       {[...participants.keys()].map((participantId, index) => (
  //         <SampleParticipantView key={index} participantId={participantId} />
  //       ))}
  //     </div>
  //   );
  // };

  // const SampleParticipantView = ({ participantId }) => {
  //   const { displayName, micOn, webcamOn, webcamStream } =
  //     useParticipant(participantId);

  //   const webcamRef = useRef(null);
  //   useEffect(() => {
  //     if (webcamRef.current) {
  //       if (webcamOn && webcamStream) {
  //         const mediaStream = new MediaStream();
  //         mediaStream.addTrack(webcamStream.track);

  //         webcamRef.current.srcObject = mediaStream;
  //         webcamRef.current
  //           .play()
  //           .catch((error) =>
  //             console.error("videoElem.current.play() failed", error)
  //           );
  //       } else {
  //         webcamRef.current.srcObject = null;
  //       }
  //     }
  //   }, [webcamStream, webcamOn]);
  //   return (
  //     <div
  //       style={{
  //         height: "300px",
  //         background: "#C0C2C9",
  //       }}
  //     >
  //       <video width={"10%"} height={"10%"} ref={webcamRef} autoPlay />
  //     </div>
  //   );
  // };
  
  const isParticipantPresenting = useMemo(() => {
    return presenterId === participantId;
  }, [presenterId, participantId]);

  const isOnStage = useMemo(() => {
    if (removedFromStageParticipantIds.includes(participantId)) return false;
    if (pinState?.share || pinState?.cam) return true;
    if (manualStageParticipantIds.includes(participantId)) return true;
    if (mMeeting?.pinnedParticipants?.has(participantId)) return true;

    const hasExplicitStage =
      (mMeeting?.pinnedParticipants?.size > 0) ||
      manualStageParticipantIds.length > 0;
    if (!hasExplicitStage) {
      const fallbackId =
        mMeeting?.activeSpeakerId ||
        mainViewParticipants?.[0] ||
        mMeeting?.localParticipant?.id;
      if (fallbackId === participantId) return true;
    }

    return false;
  }, [
    pinState,
    manualStageParticipantIds,
    removedFromStageParticipantIds,
    mainViewParticipants,
    participantId,
    mMeeting?.pinnedParticipants,
    mMeeting?.activeSpeakerId,
    mMeeting?.localParticipant?.id,
  ]);

  const canToggleParticipantMic = isLocal
    ? participantCanToggleSelfMic
    : participantCanToggleOtherMic;
  const canToggleParticipantWebcam = isLocal
    ? participantCanToggleSelfWebcam
    : participantCanToggleOtherWebcam;

  const { publish } = usePubSub(`SCR_SHR_REQ_${participantId}`);

  const [isParticipantKickoutVisible, setIsParticipantKickoutVisible] =
    useState(false);

  const [participantMode, setParticipantMode] = useState(null);
  const [moreIconClicked, setMoreIconClicked] = useState(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const meetingId = queryParams.get("meetingId");

  const handleClick = (event) => {
    setMoreIconClicked(event.currentTarget);
  };

  const handleClose = () => {
    setMoreIconClicked(null);
  };

  const addToStage = () => {
    clearRemovedFromStageParticipant(participantId);
    pin('CAM');
    addManualStageParticipant(participantId);

    setTimeout(async () => {
      console.log(mMeeting.pinnedParticipants.size);
      if (mMeeting?.isRecording && mMeeting.pinnedParticipants.size === 1) {
        const data = {
          MeetingID: meetingId,
          EndTime: getCurrentTimestamp()
        }
        await updateParticipantsEndTime(data);
      }
    }, 100); // Adjust the delay if necessary (100 ms here)
  }

  const removeFromStage = () => {
    unpin('CAM');
    removeManualStageParticipant(participantId);
    addRemovedFromStageParticipant(participantId);

    setTimeout(async () => {

      console.log(mMeeting.pinnedParticipants.size);
      if (mMeeting?.isRecording && mMeeting.pinnedParticipants.size === 0) {
        const data = {
          MeetingID: meetingId,
          StartTime: getCurrentTimestamp()
        }
        await addNoParticipantStartTime(data);
      }
    }, 100); // Adjust the delay if necessary (100 ms here)
  }

  function getCurrentTimestamp() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const isHls = useIsHls();

  // const classes = useStyles();

  const theme = useTheme();

  usePubSub(`CURRENT_MODE_${participantId}`, {
    onMessageReceived: (data) => {
      setParticipantMode(data.message);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setParticipantMode(latestMessage.message);
      }
    },
  });

  return (
    <Box
      style={{
        marginTop: '0.8vh',
        padding: '0.8vh',
        backgroundColor:
          appTheme === appThemes.DARK
            ? theme.palette.darkTheme.seven
            : appTheme === appThemes.LIGHT
              ? theme.palette.lightTheme.three
              : theme.palette.common.sidePanel,
        borderRadius: '0.6vh',
      }}
    >
      <Box
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Avatar
          variant={"rounded"}
          style={{
            width: '3.6vh',
            height: '3.6vh',
            fontSize: '1.6vh',
            color: appTheme === appThemes.LIGHT && "white",
            backgroundColor:
              appTheme === appThemes.DARK
                ? theme.palette.darkTheme.five
                : appTheme === appThemes.LIGHT
                  ? theme.palette.lightTheme.five
                  : "",
          }}
        >
          {displayName?.charAt(0)}
        </Avatar>
        {/* <SampleMeetingView /> */}
        <Box style={{ flex: 1, display: "flex", marginLeft: '0.8vh', marginRight: '0.4vh' }}>
          <Typography
            style={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              maxWidth: '8vh',
              color:
                appTheme === appThemes.LIGHT
                  ? "#95959E"
                  : "white",
              fontSize: '1.4vh',
            }}
            variant="body1"
            noWrap
          >
            {nameTructed(displayName, 10)}
          </Typography>
        </Box>

        <Box
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <Box
            style={{
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              // position: "relative",
            }}
          >
            <Box
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {raisedHand && (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: '0.4vh',
                  }}
                >
                  <RaiseHand
                    style={{ fontSize: '2vh' }}
                    fillColor={
                      appTheme === appThemes.LIGHT
                        ? "#95959E"
                        : theme.palette.common.white
                    }
                  />
                </Box>
              )}
              {canPin && (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: '0.8vh',
                    padding: '0.4vh',
                  }}
                >
                  <Tooltip
                    title={isOnStage ? "Remove from Stage" : "Add to Stage"}
                  >
                    <IconButton
                      disabled={
                        // !expanded ||
                        meetingMode === meetingModes.VIEWER ||
                        participantMode === meetingModes.VIEWER
                      }
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        isOnStage ? removeFromStage() : addToStage();
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isOnStage ? (
                        <PersonRemoveAlt1RoundedIcon color="error" style={{ fontSize: '2.2vh' }} />
                      ) : (
                        <PersonAddAlt1RoundedIcon color="primary" style={{ fontSize: '2.2vh' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {meetingMode === meetingModes.CONFERENCE && (
                <Box style={{ marginLeft: '0.4vh', marginRight: '0.4vh' }}>
                  <IconButton
                    disabled={
                      !canToggleParticipantMic ||
                      meetingMode === meetingModes.VIEWER ||
                      participantMode === meetingModes.VIEWER
                    }
                    style={{ padding: 0 }}
                    onClick={async () => {
                      if (isLocal) {
                        let track;
                        if (!micOn) {
                          track = await getCustomAudioTrack(selectMicDeviceId);
                        }
                        mMeeting?.toggleMic(track);
                      } else if (micOn) {
                        disableMic();
                      } else {
                        const track = await getCustomAudioTrack();
                        enableMic(track);
                      }
                    }}
                  >
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: 100,
                        padding: '0.4vh',
                      }}
                    >
                      {micOn ? (
                        <ParticipantMicOnIcon
                          fillColor={
                            appTheme === appThemes.LIGHT
                              ? "#95959E"
                              : "white"
                          }
                        />
                      ) : (
                        <ParticipantMicOffIcon />
                      )}
                    </Box>
                  </IconButton>
                </Box>
              )}
              {meetingMode === meetingModes.CONFERENCE && (
                <Box style={{ marginLeft: '0.8vh', marginRight: 0 }}>
                  <IconButton
                    disabled={
                      !canToggleParticipantWebcam ||
                      meetingMode === meetingModes.VIEWER ||
                      participantMode === meetingModes.VIEWER
                    }
                    style={{ padding: 0 }}
                    onClick={async () => {
                      if (isLocal) {
                        let track;
                        if (!webcamOn) {
                          track = await getCustomVideoTrack(selectWebcamDeviceId);
                        }
                        mMeeting?.toggleWebcam(track);
                      } else if (webcamOn) {
                        disableWebcam();
                      } else {
                        const track = await getCustomVideoTrack();
                        enableWebcam(track);
                      }
                    }}
                  >
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: 100,
                        padding: '0.4vh',
                      }}
                    >
                      {webcamOn ? (
                        <ParticipantVideoOnIcon
                          fillColor={
                            appTheme === appThemes.LIGHT
                              ? "#95959E"
                              : "white"
                          }
                        />
                      ) : (
                        <ParticipantVideoOffIcon />
                      )}
                    </Box>
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>

          {meetingMode === meetingModes.CONFERENCE && (
            <Box
              style={{
                transition: `all ${200 * (animationsEnabled ? 1 : 0.5)}ms`,
                marginLeft: '0.8vh',
              }}
            >
              <IconButton
                style={{ padding: 0 }}
                disabled={meetingMode === meetingModes.VIEWER}
                onClick={(e) => {
                  handleClick(e);
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 100,
                    padding: '0.4vh',
                  }}
                >
                  <MoreVert
                    fontSize="small"
                    style={{
                      height: '1.8vh',
                      width: '1.8vh',
                      color:
                        appTheme === appThemes.LIGHT
                          ? "#95959E"
                          : "white",
                    }}
                  />
                </Box>
              </IconButton>
              <Popover
                open={Boolean(moreIconClicked)}
                anchorEl={moreIconClicked}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                style={{ marginTop: '0.4vh', marginRight: '1.6vh' }}
              >
                <MenuList
                  style={{
                    backgroundColor:
                      appTheme === appThemes.DARK
                        ? theme.palette.darkTheme.slightLighter
                        : appTheme === appThemes.LIGHT
                          ? theme.palette.lightTheme.two
                          : "",
                    color:
                      appTheme === appThemes.DARK
                        ? theme.palette.common.white
                        : appTheme === appThemes.LIGHT
                          ? "#95959E"
                          : "",
                  }}
                >
                  {!isLocal && canRemoveOtherParticipant && (
                    <>
                      <MenuItem
                        key={`remove`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsParticipantKickoutVisible(true);
                          handleClose();
                        }}
                      >
                        <Box style={{ display: "flex", flexDirection: "row" }}>
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ParticipantRemoveIcon
                              fillColor={
                                appTheme === appThemes.LIGHT
                                  ? "#95959E"
                                  : theme.palette.common.white
                              }
                            />
                          </Box>
                          <Box
                            style={{
                              display: "flex",
                              flex: 1,
                              flexDirection: "column",
                              marginLeft: '1vh',
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              style={{
                                fontSize: '1.3vh',
                                color:
                                  appTheme === appThemes.LIGHT &&
                                  "#95959E",
                              }}
                            >
                              Remove Participant
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    </>
                  )}
                  {participantCanToggleOtherMode && isHls && (
                    <ToggleModeContainer
                      participantId={participantId}
                      participantMode={participantMode}
                      handleClose={handleClose}
                    />
                  )}
                  {meetingMode === meetingModes.CONFERENCE && (
                    <MenuItem
                      key={`screen-share`}
                      onClick={(e) => {
                        e.stopPropagation();
                        publish({
                          setScreenShareOn: !isParticipantPresenting,
                        });
                        handleClose();
                      }}
                      disabled={
                        !(
                          !isLocal &&
                          partcipantCanToogleOtherScreenShare &&
                          (presenterId ? isParticipantPresenting : true)
                        ) ||
                        meetingMode === meetingModes.VIEWER ||
                        participantMode === meetingModes.VIEWER
                      }
                    >
                      <Box style={{ display: "flex", flexDirection: "row" }}>
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isParticipantPresenting ? (
                            <ParticipantScreenShareIcon
                              fillColor={
                                appTheme === appThemes.LIGHT
                                  ? "#95959E"
                                  : theme.palette.common.white
                              }
                            />
                          ) : (
                            <ParticipantScreenShareIcon
                              fillColor={
                                appTheme === appThemes.LIGHT
                                  ? "#95959E"
                                  : !(
                                    !isLocal &&
                                    partcipantCanToogleOtherScreenShare &&
                                    (presenterId
                                      ? isParticipantPresenting
                                      : true)
                                  ) ||
                                    meetingMode === meetingModes.VIEWER ||
                                    participantMode === meetingModes.VIEWER
                                    ? appTheme === appThemes.LIGHT
                                      ? "#95959E"
                                      : "#ffffff80"
                                    : appTheme === appThemes.LIGHT
                                      ? "#95959E"
                                      : "#ffffff"
                              }
                            />
                          )}
                        </Box>
                        <Box
                          style={{
                            display: "flex",
                            flex: 1,
                            flexDirection: "column",
                            marginLeft: '1vh',
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: '1.3vh',
                              color:
                                !(
                                  !isLocal &&
                                  partcipantCanToogleOtherScreenShare &&
                                  (presenterId ? isParticipantPresenting : true)
                                ) ||
                                  meetingMode === meetingModes.VIEWER ||
                                  participantMode === meetingModes.VIEWER
                                  ? theme.palette.text.secondary
                                  : appTheme === appThemes.LIGHT
                                    ? "#95959E"
                                    : theme.palette.common.white,
                            }}
                          >
                            {isParticipantPresenting
                              ? "Stop Screenshare"
                              : "Request Screenshare"}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  )}
                </MenuList>
              </Popover>
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmBox
        open={isParticipantKickoutVisible}
        title={`Remove ${nameTructed(displayName, 15)} `}
        subTitle={`Are you sure want to remove ${nameTructed(
          displayName,
          15
        )} from the call?`}
        successText={"Remove"}
        rejectText={"Cancel"}
        onSuccess={() => {
          participant.remove();
          setIsParticipantKickoutVisible(false);
        }}
        onReject={() => {
          setIsParticipantKickoutVisible(false);
        }}
      />
    </Box>
  );
}

export default function ParticipantsTabPanel({ panelWidth, panelHeight }) {
  const [filterQuery, setFilterQuery] = useState("");

  const { participants } = useMeeting();
  const { raisedHandsParticipants, appTheme } = useMeetingAppContext();

  const sortedRaisedHandsParticipants = useMemo(() => {
    const participantIds = [...participants.keys()];

    const notRaised = participantIds.filter(
      (pID) =>
        raisedHandsParticipants.findIndex(
          ({ participantId: rPID }) => rPID === pID
        ) === -1
    );

    const raisedSorted = raisedHandsParticipants.sort((a, b) => {
      if (a.raisedHandOn > b.raisedHandOn) {
        return -1;
      }
      if (a.raisedHandOn < b.raisedHandOn) {
        return 1;
      }
      return 0;
    });

    const combined = [
      ...raisedSorted.map(({ participantId: p }) => ({
        raisedHand: true,
        participantId: p,
      })),
      ...notRaised.map((p) => ({ raisedHand: false, participantId: p })),
    ];

    return combined;
  }, [raisedHandsParticipants, participants]);

  const theme = useTheme();

  const filterParticipants = (
    sortedRaisedHandsParticipants,
    participants,
    filterQuery
  ) =>
    filterQuery?.length > 3
      ? sortedRaisedHandsParticipants.filter(({ participantId }) => {
        const { displayName } = participants.get(participantId);

        const hide = !displayName
          ?.toLowerCase()
          .includes(filterQuery.toLowerCase());

        return !hide;
      })
      : sortedRaisedHandsParticipants;

  const part = useMemo(
    () => {
      const filtered = filterParticipants(
        sortedRaisedHandsParticipants,
        participants,
        filterQuery
      );
      console.log('Participants to render:', filtered);
      console.log('Total participants count:', participants.size);
      return filtered;
    },

    [sortedRaisedHandsParticipants, participants, filterQuery]
  );

  function rowRenderer({ key, index, isScrolling, isVisible, style }) {
    const { participantId, raisedHand } = part[index];

    return (
      <div {...{ key, style }}>
        <ParticipantListItem
          participantId={participantId}
          raisedHand={raisedHand}
        />
      </div>
    );
  }

  const { width } = useWindowSize();
  const isTab = useIsTab();
  const isMobile = useIsMobile();
  const outerTheme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guest = queryParams.get("guest");

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        mode: outerTheme.palette.mode,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": "#70707033",
              "--TextField-brandBorderHoverColor": "#70707033",
              "--TextField-brandBorderFocusedColor": "#70707033",
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: appTheme !== appThemes.LIGHT && "#404B53",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor:
                  appTheme === appThemes.LIGHT
                    ? "var(--TextField-brandBorderHoverColor)"
                    : "white",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor:
                  appTheme === appThemes.LIGHT
                    ? "var(--TextField-brandBorderFocusedColor)"
                    : "white",
                borderWidth: "1px",
              },
              color: appTheme === appThemes.LIGHT ? "#404B53" : "white",
            },
          },
        },
      },
    });

  return (
    <Box
      style={{
        height: panelHeight,
        display: 'flex',
        flexDirection: 'column',
        padding: '0.8vh',
        gap: '0.8vh',
      }}
    >
      <Box style={{ flex: 0 }}>
        <ThemeProvider theme={customTheme(outerTheme)}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Search Participants"
            style={{
              color:
                appTheme === appThemes.LIGHT &&
                "#95959E",
              borderRadius: "0.6vh",
            }}
            onChange={(e) => setFilterQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined
                    style={{
                      color:
                        appTheme === appThemes.LIGHT
                          ? "#95959E"
                          : "white",
                      fontSize: '2.2vh',
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-input::placeholder': {
                fontSize: '1.8vh',
                opacity: 0.7,
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '1.8vh',
                padding: '1.4vh 0vh',
              }
            }}
          />
        </ThemeProvider>
      </Box>
      <Box style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {part.map(({ participantId, raisedHand }, index) => (
          <ParticipantListItem
            key={participantId}
            participantId={participantId}
            raisedHand={raisedHand}
          />
        ))}
      </Box>
      <Box style={{ flex: 0, display: 'flex', flexDirection: 'column', gap: '0.4vh' }}>
        <Typography style={{
          color: 'white',
          fontWeight: '600',
          fontSize: '1.8vh',
        }}>Participants Queue</Typography>
        <RequestedEntries />
      </Box>
      {guest !== 'true' &&
        <ConfigTabPanel />
      }
    </Box>
  );
}
